import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import firebase from "services/firebase/firebase.config";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useAuthStore } from "stores/authStore/authStore";
import { InputField } from "components/designV2/inputfield/inputfield";
import { CompanyRole } from "model/User";
import {
  COMPANY_AUTH_PRIMARY_BUTTON_STYLES,
  COMPANY_AUTH_SECONDARY_BUTTON_STYLES,
} from "web-apps/company/components/shared/companyAuth.buttonStyles.contract";
import {
  CreateAccountButton,
  ErrorText,
  FieldsWrapper,
  FormStyled,
  LoginButton,
  PageActions,
} from "./loginForm.styled";

type Props = {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  onCreateAccount: () => void;
};

export const LoginForm: React.FC<Props> = ({
  email,
  setEmail,
  onCreateAccount,
}) => {
  const { t } = useTranslation();
  const [authState, , authDispatch] = useAuthStore();
  const [companyState] = useCompanyStore();
  const [password, setPassword] = useState("");
  const [hasLoginError, setHasLoginError] = useState(false);
  const trimmedEmail = email.trim();
  const canLogIn = Boolean(trimmedEmail && password);

  useEffect(() => {
    setHasLoginError(false);
  }, [email, password]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (canLogIn) {
      authDispatch({ type: "SET_IS_LOADING", payload: true });
      try {
        await firebase
          .auth()
          .signInWithEmailAndPassword(trimmedEmail, password);
      } catch {
        setHasLoginError(true);
        authDispatch({ type: "SET_IS_LOADING", payload: false });
      }
    }
  };

  return (
    <>
      {authState.signedIn && companyState.companies.length > 0 && (
        <Redirect
          to={
            companyState.companies.length > 1
              ? "./company-select"
              : authState.user?.role === CompanyRole.COMPANY_TIMESTAMP
                ? "./stampclock"
                : "./schedule"
          }
        />
      )}
      <FormStyled onSubmit={handleLogin}>
        <FieldsWrapper>
          <InputField
            label={t("LoginCompany.Email")}
            type="email"
            value={email}
            placeholder={t("LoginCompany.Email")}
            fullWidth
            onChange={setEmail}
            onClear={() => setEmail("")}
            disabled={authState.isLoading}
          />
          <InputField
            label={t("LoginCompany.Password")}
            type="password"
            value={password}
            placeholder={t("LoginCompany.Password")}
            fullWidth
            onChange={setPassword}
            onClear={() => setPassword("")}
            disabled={authState.isLoading}
          />
        </FieldsWrapper>
        {hasLoginError && (
          <ErrorText>{t("LoginCompany.EmailPasswordCombination")}</ErrorText>
        )}
        <PageActions>
          <LoginButton
            type="submit"
            label={t("LoginCompany.Login")}
            disabled={!canLogIn}
            isLoading={authState.isLoading}
            styleOverrides={COMPANY_AUTH_PRIMARY_BUTTON_STYLES}
          />
          <CreateAccountButton
            label={t("LoginCompany.CreateAccount")}
            onClick={onCreateAccount}
            disabled={authState.isLoading}
            styleOverrides={COMPANY_AUTH_SECONDARY_BUTTON_STYLES}
          />
        </PageActions>
      </FormStyled>
    </>
  );
};
